import { useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Search,
  CheckCircle2,
  Clock,
  Phone,
  Truck,
  PackageCheck,
  XCircle,
  CalendarDays,
  MapPin,
  Camera,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_FLOW = [
  { key: "new", label: "Received", icon: CheckCircle2 },
  { key: "contacted", label: "Contacted", icon: Phone },
  { key: "scheduled", label: "Scheduled", icon: CalendarDays },
  { key: "completed", label: "Completed", icon: PackageCheck },
];

const moveTypeLabel = {
  local: "Local Move",
  long_distance: "Long Distance",
  storage: "Storage",
  commercial: "Commercial",
  specialty: "Specialty",
  packing: "Packing Only",
  freight_container: "Container / Trailer Load",
};
const homeSizeLabel = {
  studio: "Studio",
  "1br": "1 Bedroom",
  "2br": "2 Bedroom",
  "3br": "3 Bedroom",
  "4br_plus": "4+ Bedroom",
  office: "Office / Commercial",
};
const serviceLabel = {
  packing: "Packing",
  unpacking: "Unpacking",
  piano: "Piano",
  antiques: "Antiques",
  storage_30: "30-day Storage",
  appliance_install: "Appliance Install",
};

export default function TrackBooking() {
  const [confirmation, setConfirmation] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setResult(null);
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/lookup", {
        confirmation_id: confirmation,
        email,
      });
      setResult(data);
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || "Lookup failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const refreshResult = async () => {
    try {
      const { data } = await api.post("/bookings/lookup", {
        confirmation_id: confirmation,
        email,
      });
      setResult(data);
    } catch (e) {
      // silent
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EC]" data-testid="track-page">
      <Header />
      <main>
        <section className="py-20 lg:py-28 border-b border-[#DDD8C9]">
          <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="overline">Track Your Move</div>
              <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                Where's<br />
                your <span className="text-[#5BB239]">move</span>?
              </h1>
              <p className="mt-6 text-[#5C635F] leading-relaxed max-w-md">
                Pop in your 8-character confirmation ID and the email you booked
                with — we'll show you your current status and details.
              </p>

              <div className="mt-10 bg-white border border-[#DDD8C9] p-6">
                <div className="overline">Lost your ID?</div>
                <p className="mt-2 text-sm text-[#5C635F]">
                  Check your booking confirmation email, or call dispatch at
                  <a href="tel:+15025077904" className="text-[#5BB239] font-bold ml-1">
                    (502) 507-7904
                  </a>.
                </p>
              </div>
            </div>

            <div className="lg:col-span-3">
              <form
                onSubmit={submit}
                className="bg-white border border-[#DDD8C9] p-6 lg:p-10 space-y-5"
              >
                <div className="flex items-center gap-2 pb-4 border-b border-[#DDD8C9]">
                  <Search className="w-4 h-4 text-[#5BB239]" />
                  <span className="font-mono-label text-xs">Lookup Form</span>
                </div>

                <div>
                  <Label className="font-mono-label text-[10px] text-[#5C635F]">
                    Confirmation ID
                  </Label>
                  <Input
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value.toUpperCase())}
                    required
                    placeholder="e.g. 871EF4AE"
                    className="mt-2 rounded-none border-[#DDD8C9] h-12 tracking-[0.2em] font-mono uppercase"
                    data-testid="track-confirmation-input"
                  />
                </div>

                <div>
                  <Label className="font-mono-label text-[10px] text-[#5C635F]">
                    Email used at booking
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2 rounded-none border-[#DDD8C9] h-12"
                    data-testid="track-email-input"
                  />
                </div>

                {err && (
                  <div
                    role="alert"
                    data-testid="track-error"
                    className="bg-red-50 border-l-2 border-red-500 px-3 py-2 text-sm text-red-700"
                  >
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="track-submit-btn"
                  className="w-full bg-[#5BB239] text-white py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#499828] transition-colors disabled:opacity-60"
                >
                  {loading ? "Searching…" : "Find My Booking"}
                </button>
              </form>

              {result && <ResultCard r={result} email={email} onChange={refreshResult} />}

              <p className="mt-6 text-xs text-[#5C635F]">
                No booking yet?{" "}
                <Link to="/#book" className="text-[#5BB239] font-bold hover:underline">
                  Book a move →
                </Link>
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function ResultCard({ r, email, onChange }) {
  const isCancelled = r.status === "cancelled";
  const currentIdx = isCancelled
    ? -1
    : STATUS_FLOW.findIndex((s) => s.key === r.status);

  return (
    <div className="mt-6 bg-[#223932] text-white" data-testid="track-result">
      <div className="p-6 lg:p-10 border-b border-white/10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono-label text-[10px] text-[#5BB239]">
              Confirmation
            </div>
            <div
              className="mt-1 font-display font-black text-3xl tracking-[0.15em]"
              data-testid="track-result-id"
            >
              {r.confirmation_id}
            </div>
            <div className="mt-3 text-sm text-white/70">
              Hi {r.full_name.split(" ")[0]} — here's where we're at:
            </div>
          </div>
          <StatusBadge status={r.status} />
        </div>
      </div>

      {/* Timeline */}
      <div className="p-6 lg:p-10 border-b border-white/10">
        {isCancelled ? (
          <div className="flex items-center gap-3 text-white/60">
            <XCircle className="w-5 h-5 text-red-400" />
            <span className="font-mono-label text-xs">This booking has been cancelled</span>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {STATUS_FLOW.map((s, i) => {
              const done = i <= currentIdx;
              const isCurrent = i === currentIdx;
              const Icon = s.icon;
              return (
                <div key={s.key} className="flex flex-col gap-3">
                  <div
                    className={`h-1 w-full ${
                      done ? "bg-[#5BB239]" : "bg-white/15"
                    }`}
                  />
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 flex items-center justify-center ${
                        done ? "bg-[#5BB239]" : "bg-white/10"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <div
                        className={`font-mono-label text-[9px] ${
                          isCurrent ? "text-[#5BB239]" : "text-white/50"
                        }`}
                      >
                        Step 0{i + 1}
                      </div>
                      <div
                        className={`font-display font-bold text-sm ${
                          done ? "text-white" : "text-white/40"
                        }`}
                      >
                        {s.label}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="p-6 lg:p-10 grid sm:grid-cols-2 gap-6">
        <Detail label="Move Type" value={moveTypeLabel[r.move_type] || r.move_type} />
        <Detail label="Home Size" value={homeSizeLabel[r.home_size] || r.home_size} />
        <Detail
          label="Route"
          value={
            <span className="flex items-center gap-2 flex-wrap">
              <MapPin className="w-3.5 h-3.5 text-[#5BB239]" />
              {r.origin_city}, {r.origin_state}
              <span className="text-[#5BB239]">→</span>
              {r.destination_city}, {r.destination_state}
            </span>
          }
        />
        <Detail
          label="Moving Date"
          value={
            <span className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-[#5BB239]" />
              {r.move_date}
            </span>
          }
        />

        {r.services && r.services.length > 0 && (
          <div className="sm:col-span-2">
            <div className="font-mono-label text-[10px] text-white/50">
              Add-ons
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {r.services.map((s) => (
                <span
                  key={s}
                  className="font-mono-label text-[10px] bg-white/10 px-3 py-1.5"
                >
                  {serviceLabel[s] || s}
                </span>
              ))}
            </div>
          </div>
        )}

        {r.estimated_quote && (
          <div className="sm:col-span-2 border-t border-white/10 pt-6">
            <div className="font-mono-label text-[10px] text-white/50">
              Estimated Range
            </div>
            <div className="mt-1 font-display font-black text-3xl tracking-tight">
              ${r.estimated_quote.low.toLocaleString()} — $
              {r.estimated_quote.high.toLocaleString()}
            </div>
          </div>
        )}

        {r.deposit_status && (
          <div className="sm:col-span-2 border-t border-white/10 pt-6" data-testid="track-deposit-block">            <div className="font-mono-label text-[10px] text-white/50">
              Deposit · ${r.deposit_amount || 100}
            </div>
            {r.deposit_status === "paid" ? (
              <div className="mt-2 inline-flex items-center gap-2 bg-emerald-600/20 border border-emerald-400/40 px-3 py-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <div>
                  <div className="font-display font-black text-lg tracking-tight text-emerald-300">Deposit Received · Date Locked</div>
                  <div className="text-[11px] text-white/60 mt-0.5">
                    {r.deposit_paid_at ? `Paid ${new Date(r.deposit_paid_at).toLocaleDateString()}` : ""}
                  </div>
                </div>
              </div>
            ) : r.payment_link ? (
              <div className="mt-2 bg-amber-500/15 border border-amber-400/40 p-4 max-w-lg">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-display font-black text-lg tracking-tight text-amber-200">
                      Pay $100 to lock your date
                    </div>
                    <div className="text-[11px] text-white/70 mt-1 leading-relaxed">
                      Your QuickBooks invoice is ready. Applied to your final bill.
                    </div>
                  </div>
                </div>
                <a
                  href={r.payment_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 flex items-center justify-center gap-2 bg-[#5BB239] hover:bg-[#499828] text-white px-5 py-3 font-bold uppercase tracking-widest text-xs transition-colors"
                  data-testid="track-pay-deposit-btn"
                >
                  Pay $100 Deposit
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M7 17L17 7M17 7H8M17 7V16"/></svg>
                </a>
              </div>
            ) : (
              <div className="mt-2 inline-flex items-start gap-2 bg-amber-500/15 border border-amber-400/40 px-3 py-2 max-w-lg">
                <Clock className="w-4 h-4 text-amber-300 mt-0.5" />
                <div>
                  <div className="font-display font-black text-lg tracking-tight text-amber-200">Awaiting $100 Deposit</div>
                  <div className="text-[11px] text-white/70 mt-1 leading-relaxed">
                    Check your email for a QuickBooks invoice within 30 minutes. Once paid, your date is officially locked.
                    Need help? Call (502) 507-7904.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {r.referral_code_owned && (
          <div className="sm:col-span-2 border-t border-white/10 pt-6" data-testid="track-referral-block">
            <div className="font-mono-label text-[10px] text-[#5BB239] tracking-widest">
              🎁 Your Referral Code
            </div>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <div
                className="font-display font-black text-2xl tracking-[0.15em] text-white bg-[#5BB239]/15 border border-[#5BB239]/40 px-4 py-2"
                data-testid="track-referral-code"
              >
                {r.referral_code_owned}
              </div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(r.referral_code_owned);
                  toast.success("Code copied — share it anywhere!");
                }}
                className="text-[10px] font-mono-label uppercase tracking-widest text-[#5BB239] hover:text-white flex items-center gap-1.5"
                data-testid="track-referral-copy"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                Copy
              </button>
              <button
                type="button"
                onClick={async () => {
                  const url = `${window.location.origin}/?ref=${r.referral_code_owned}#book`;
                  const text = `I used ThemBoysMoving for my move — use my code ${r.referral_code_owned} at booking and we both get $25 off!`;
                  if (navigator.share) {
                    try { await navigator.share({ title: "ThemBoysMoving Referral", text, url }); } catch {}
                  } else {
                    navigator.clipboard.writeText(`${text} ${url}`);
                    toast.success("Message copied — paste it in a text!");
                  }
                }}
                className="text-[10px] font-mono-label uppercase tracking-widest text-white bg-[#5BB239] hover:bg-[#499828] px-3 py-1.5 flex items-center gap-1.5 transition-colors"
                data-testid="track-referral-share"
              >
                Share Link
              </button>
            </div>
            <div className="mt-2 text-[11px] text-white/60 leading-relaxed max-w-md">
              Share with a friend — they get <strong className="text-white">$25 off</strong> their move, and you get <strong className="text-white">$25 credit</strong> on your next one.
            </div>
          </div>
        )}
      </div>

      <PhotoSection r={r} email={email} onChange={onChange} />

      <div className="px-6 lg:px-10 py-5 bg-[#151816] flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-white/50">
          Questions? Call dispatch:{" "}
          <a href="tel:+15025077904" className="text-white font-bold">
            (502) 507-7904
          </a>
        </span>
        <Truck className="w-5 h-5 text-[#5BB239]" />
      </div>
    </div>
  );
}

function PhotoSection({ r, email, onChange }) {
  const [uploading, setUploading] = useState(false);
  const photos = r.photos || [];

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let successCount = 0;
    for (const file of files) {
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name} is too big (max 8MB)`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/bookings/${r.id}/photos?email=${encodeURIComponent(email)}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        successCount += 1;
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || `Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} photo${successCount > 1 ? "s" : ""}`);
      onChange && onChange();
    }
  };

  return (
    <div className="border-t border-white/10 p-6 lg:p-10" data-testid="track-photo-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-mono-label text-[10px] text-[#5BB239]">Inventory Photos</div>
          <div className="mt-1 font-display font-bold text-2xl">
            {photos.length === 0
              ? "Snap your stuff."
              : `${photos.length} photo${photos.length > 1 ? "s" : ""} uploaded`}
          </div>
        </div>
        <label
          className={`inline-flex items-center gap-2 bg-[#5BB239] text-white px-5 py-3 font-mono-label text-[11px] cursor-pointer hover:bg-[#499828] transition-colors ${
            uploading ? "opacity-60 pointer-events-none" : ""
          }`}
          data-testid="track-upload-label"
        >
          <Upload className="w-3.5 h-3.5" />
          {uploading ? "Uploading…" : "Upload Photos"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            disabled={uploading}
            onChange={(e) => {
              handleFiles(Array.from(e.target.files || []));
              e.target.value = "";
            }}
            className="hidden"
            data-testid="track-upload-input"
          />
        </label>
      </div>

      <p className="text-xs text-white/60 leading-relaxed mb-6 max-w-xl">
        Help us nail your estimate — share photos of your largest items, tight stairwells, or
        anything we should plan for. JPG, PNG, or WEBP. 8MB max each.
      </p>

      {photos.length === 0 ? (
        <div className="border border-dashed border-white/15 p-10 text-center" data-testid="photo-empty-state">
          <Camera className="w-8 h-8 text-white/30 mx-auto" />
          <p className="mt-3 text-xs text-white/40 font-mono-label">No photos yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photo-grid">
          {photos.map((p) => (
            <PhotoTile
              key={p.id}
              photo={p}
              bookingId={r.id}
              email={email}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoTile({ photo, bookingId, email }) {
  const src = `${API}/bookings/${bookingId}/photos/${photo.id}?email=${encodeURIComponent(email)}`;
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block bg-black/30 aspect-square overflow-hidden border border-white/10 hover:border-[#5BB239] transition-colors"
      data-testid={`photo-tile-${photo.id}`}
    >
      <img
        src={src}
        alt={photo.original_filename || "Inventory"}
        className="w-full h-full object-cover transition-opacity group-hover:opacity-80"
        loading="lazy"
      />
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
        <div className="font-mono-label text-[9px] text-white/80 truncate">
          {photo.original_filename}
        </div>
      </div>
    </a>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="font-mono-label text-[10px] text-white/50">{label}</div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}

const STATUS_LABEL = {
  new: "Received",
  contacted: "Contacted",
  scheduled: "Scheduled",
  completed: "Completed",
  cancelled: "Cancelled",
};
const STATUS_COLOR = {
  new: "bg-[#5BB239]",
  contacted: "bg-amber-500",
  scheduled: "bg-blue-500",
  completed: "bg-emerald-600",
  cancelled: "bg-zinc-500",
};

function StatusBadge({ status }) {
  return (
    <div
      className={`${STATUS_COLOR[status]} px-4 py-2 font-mono-label text-[10px] uppercase text-white`}
      data-testid="track-result-status"
    >
      {STATUS_LABEL[status]}
    </div>
  );
}
